import { useEffect, useState } from "react";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  AdminPageLayout,
  AdminSectionCard,
  AdminAction,
  AdminFormFooter,
} from "../../components/admin";
import { resolveMediaUrl } from "../../utils/mediaUrl";
import { createBanner, getBanner, updateBanner } from ".";

const ACCEPTED_TYPES = ["image/png", "image/webp", "image/jpeg"];
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

function getApiErrorMessage(error, fallback) {
  return (
    error.response?.data?.message ||
    error.response?.data?.data?.message ||
    error.message ||
    fallback
  );
}

export default function BannerFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [redirectUrl, setRedirectUrl] = useState("");
  const [file, setFile] = useState(null);
  const [image, setImage] = useState("");
  const [existingImagePath, setExistingImagePath] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isEdit) return;

    let cancelled = false;
    (async () => {
      setFetching(true);
      try {
        const response = await getBanner(id);
        const banner = response.data?.data;
        if (cancelled) return;
        if (!banner) {
          toast.error("Banner not found", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
          });
          navigate("/banners", { replace: true });
          return;
        }
        setTitle(banner.name || "");
        setRedirectUrl(banner.redirectionUrl || "");
        setExistingImagePath(banner.imageUrl || "");
        setImage(resolveMediaUrl(banner.imageUrl));
      } catch (error) {
        if (!cancelled) {
          toast.error(getApiErrorMessage(error, "Failed to load banner"), {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: true,
          });
          navigate("/banners", { replace: true });
        }
      } finally {
        if (!cancelled) setFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit, navigate]);

  const applyImageFile = (selectedFile) => {
    if (!selectedFile) return;
    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      toast.error("Image type should be PNG, WEBP, or JPEG", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }
    if (selectedFile.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be 5MB or smaller", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = (e) => setImage(e.target.result);
    reader.readAsDataURL(selectedFile);
  };

  const changeHandler = (event) => {
    applyImageFile(event.target.files?.[0]);
    event.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    applyImageFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.error("Title is required", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }
    if (!isEdit && !file) {
      toast.error("Upload image first", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }
    if (isEdit && !file && !existingImagePath) {
      toast.error("Upload image first", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", trimmedTitle);
      formData.append("duration", 5);
      formData.append("redirectionUrl", redirectUrl.trim());
      if (file) {
        formData.append("image", file);
      }

      if (isEdit) {
        await updateBanner(id, formData);
        navigate("/banners", { replace: true, state: { updated: true } });
      } else {
        await createBanner(formData);
        navigate("/banners", { replace: true, state: { results: true } });
      }
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, isEdit ? "Failed to update banner" : "Failed to create banner"),
        {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: true,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminPageLayout
      eyebrow="Homepage Promotion"
      title={isEdit ? "Edit Banner" : "Add Banner"}
      description="Upload a banner image and define where users should be redirected from the homepage."
      actions={
        <AdminAction
          variant="outline"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate("/banners")}
        >
          Back to listing
        </AdminAction>
      }
    >
      <AdminSectionCard
        title="Banner details"
        description={
          isEdit
            ? "Update the title, redirect URL, and image for this banner."
            : "Add a title, optional redirect URL, and banner image."
        }
      >
        {fetching ? (
          <div className="admin-empty-state">Loading banner…</div>
        ) : (
          <>
            <div className="admin-form-body overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-sm font-medium leading-6 text-stone-700 mb-3">
                    Banner Image{isEdit ? "" : " *"}
                  </label>
                  <div
                    className={`min-h-[200px] border-2 border-dashed rounded-xl bg-stone-50 flex flex-col items-center justify-center cursor-pointer transition-colors hover:bg-stone-100 hover:border-stone-400 ${
                      isDragging ? "border-stone-900 bg-stone-100" : "border-stone-300"
                    }`}
                    onClick={() => document.getElementById("banner-image-input").click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragging(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragging(false);
                    }}
                    onDrop={handleDrop}
                  >
                    <input
                      id="banner-image-input"
                      type="file"
                      accept="image/png, image/webp, image/jpeg"
                      onChange={changeHandler}
                      className="hidden"
                    />
                    {image ? (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <img
                          src={image}
                          alt="Preview"
                          className="max-h-[180px] object-contain rounded-lg"
                          onError={() => setImage("")}
                        />
                      </div>
                    ) : (
                      <div className="text-center p-6">
                        <svg
                          className="mx-auto h-12 w-12 text-stone-400 mb-3"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-stone-700 font-medium">
                          Click or drag file to this area to upload
                        </p>
                        <p className="text-stone-400 text-sm mt-1">PNG, WEBP, JPEG (max 5MB)</p>
                      </div>
                    )}
                  </div>
                  {isEdit ? (
                    <p className="mt-2 text-xs text-stone-500">
                      Leave unchanged to keep the current image, or upload a new file to replace it.
                    </p>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={title}
                      placeholder="Enter banner title"
                      className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4"
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium leading-6 text-stone-700 mb-2">
                      Redirect URL
                    </label>
                    <input
                      type="text"
                      value={redirectUrl}
                      placeholder="https://example.com"
                      className="block w-full rounded-xl border-0 py-2.5 text-stone-900 shadow-sm ring-1 ring-inset ring-stone-300 placeholder:text-stone-400 focus:ring-2 focus:ring-inset focus:ring-stone-900 sm:text-sm sm:leading-6 transition-shadow px-4"
                      onChange={(e) => setRedirectUrl(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <AdminFormFooter>
              <AdminAction onClick={handleSubmit} loading={loading}>
                {isEdit ? "Update Banner" : "Add Banner"}
              </AdminAction>
            </AdminFormFooter>
          </>
        )}
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
